import {
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core';
import { useState } from 'react';
import Link from '@/components/Link';
import {
  LEAD_MQL_STATUSES,
  type LeadMqlStatus,
  useAssignees,
  useAssignLead,
  useUpdateLead,
} from '@/models/leads';
import { useHasAnyPermission } from '@/models/permissions';
import { useInbox } from '@/models/persons';
import { CasesList } from '@/pages/persons/CasesPage';

const fmt = (v: string) => new Date(v).toLocaleString();

export default function InboxPage() {
  const [view, setView] = useState<'unassigned' | 'spam' | 'duplicates'>(
    'unassigned',
  );
  const canMerge = useHasAnyPermission([
    'contacts:assign',
    'contacts:*',
    '*:*',
  ]);
  const { data } = useInbox(view === 'duplicates' ? 'unassigned' : view);
  const assignees = useAssignees();
  const assign = useAssignLead();
  const update = useUpdateLead();
  const rows = data?.rows ?? [];
  const people = (assignees.data?.users ?? []).map((u) => ({
    value: String(u.id),
    label: u.info?.name || u.email,
  }));
  return (
    <Paper p="md" h="100%">
      <Stack gap="sm">
        <Tabs
          value={view}
          onChange={(v) =>
            setView((v as 'unassigned' | 'spam' | 'duplicates') ?? 'unassigned')
          }
        >
          <Tabs.List>
            <Tabs.Tab value="unassigned">Without owner</Tabs.Tab>
            <Tabs.Tab value="spam">Spam of 7 days</Tabs.Tab>
            {canMerge && (
              <Tabs.Tab value="duplicates">Possible duplicates</Tabs.Tab>
            )}
          </Tabs.List>
        </Tabs>
        {view === 'duplicates' ? (
          <CasesList codes={['phone_match', 'email_taken']} />
        ) : (
          <Table verticalSpacing={6} fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Request</Table.Th>
                <Table.Th>Person</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Owner</Table.Th>
                <Table.Th>MQL</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Link to={`~/leads/${r.id}`}>#{r.id}</Link>
                    <Text size="xs" c="dimmed">
                      {fmt(r.createdAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Text size="sm">
                        {r.personName ?? r.email ?? r.phone}
                      </Text>
                      {r.repeat && (
                        <Badge size="xs" color="orange" variant="light">
                          Repeat
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {r.request ?? ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>{r.source}</Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      w={170}
                      placeholder="Assign"
                      data={people}
                      searchable
                      onChange={(v) =>
                        v &&
                        assign.mutate({
                          leadId: r.id,
                          assigneeUserId: Number(v),
                        })
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      w={150}
                      placeholder="MQL"
                      data={LEAD_MQL_STATUSES}
                      value={r.mqlStatus}
                      onChange={(v) =>
                        update.mutate({
                          id: r.id,
                          mqlStatus: v as LeadMqlStatus | null,
                        })
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-xs"
                      variant="light"
                      color={r.isSpam ? 'gray' : 'red'}
                      onClick={() =>
                        update.mutate({ id: r.id, isSpam: !r.isSpam })
                      }
                    >
                      {r.isSpam ? 'Not spam' : 'Spam'}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        {view !== 'duplicates' && rows.length === 0 && (
          <Text size="sm" c="dimmed">
            Nothing waiting.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
